import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import './Dashboard.css';
import axios from 'axios';
import { fetchOffers, handleLoginWithYouTube, fetchDataByDateRange, logoutFromYt } from '../../utils/apiCalls';
import { useRevit } from '../../contexts/RevitContext';
import { useAuth } from '@clerk/clerk-react';
import { NavLink } from 'react-router-dom';
import { getCurrencySymbol } from '../../utils/currencyUtils';
import Icon from '../Icon';
import { 
    ResponsiveContainer, 
    AreaChart, 
    Area, 
    CartesianGrid, 
    XAxis, 
    YAxis, 
    Tooltip, 
    Legend 
} from 'recharts';
import { Share2, Download, X, GripVertical, Columns3, ChevronRight, Trash2, Sigma } from 'lucide-react';
import html2canvas from 'html2canvas';
import ShareCard from './ShareCard/ShareCard';
import DashboardFilterRail from './filters/DashboardFilterRail';
import useColumnDragReorder from '../../hooks/useColumnDragReorder';
import {
    COLUMN_SAVE_DELAY_MS,
    STATIC_COLUMN_DEFAULTS,
    buildDefaultOrder,
    columnLabel,
    isColumnVisible,
    mergeColumnOrder,
    normalizeVisibleColumns,
    readLocalColumnPrefs,
} from './columnPrefs';
import { columnCsvHeader, columnCsvValue, renderColumnCell, renderColumnHeader, renderColumnTotal } from './DashboardColumns';

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

const CACHE_EXPIRATION_TIME = 5 * 60 * 1000; // 5 minutes in milliseconds

const isYoutubeResource = (category) => (
    category === 'video' || category === 'pinComment' || category === 'qr'
);

const getYoutubeVideoId = (resource) => (
    resource.youtube_video_id
    || (resource.category === 'video' || resource.category === 'pinComment' ? resource.name : null)
);

const hasValidSource = (resource) => {
    const category = resource?.category;
    const name = resource?.name;
    if (category == null || String(category).trim() === '' || category === 'NA') return false;
    if (name == null || String(name).trim() === '' || name === 'NA') return false;
    return true;
};

const Dashboard = ({ isSidebarOpen }) => {
    const [filterType, setFilterType] = useState('all');
    const [sortConfig, setSortConfig] = useState({ key: 'first_event', direction: 'descending' });
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [selectedOffer, setSelectedOffer] = useState('all');
    const [toggledRows, setToggledRows] = useState({});
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [isDateFiltered, setIsDateFiltered] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [loggingOutYt, setLoggingOutYt] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [resourceToDelete, setResourceToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [offersCache, setOffersCache] = useState({});
    const [salesCache, setSalesCache] = useState({});
    const [offers, setOffers] = useState([]);
    const [currency, setCurrency] = useState('USD');
    const [salesData, setSalesData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [deleteConfirmationText, setDeleteConfirmationText] = useState('');
    const [hasLandingPages, setHasLandingPages] = useState(false);
    const [confirmationText, setConfirmationText] = useState('');
    // Add new state variables for published date filtering
    const [publishedStartDate, setPublishedStartDate] = useState(null);
    const [publishedEndDate, setPublishedEndDate] = useState(null);
    const [isPublishedDateFiltered, setIsPublishedDateFiltered] = useState(false);
    const [selectedTags, setSelectedTags] = useState([]);
    const [filterMode, setFilterMode] = useState('clicks'); // 'clicks' or 'published'
    const [isDeletingData, setIsDeletingData] = useState(false);
    // Add state for showing totals
    const [showTotals, setShowTotals] = useState(true);
    // Add new state for data filtering
    const [dataFilter, setDataFilter] = useState({
        column: '',
        operator: 'above',
        value: ''
    });
    const [showLinkPopup, setShowLinkPopup] = useState(false);
    const [linkingResource, setLinkingResource] = useState(null);
    const [linkUrl, setLinkUrl] = useState('');
    const [editingPublishedDate, setEditingPublishedDate] = useState(null);
    const [tempPublishedDate, setTempPublishedDate] = useState('');
    const [addingTagId, setAddingTagId] = useState(null);
    const [tagDraft, setTagDraft] = useState('');
    const [savingTagId, setSavingTagId] = useState(null);
    const tagEditCancelled = useRef(false);
    const [resourceData, setResourceData] = useState({});
    const [aggregateVideoData, setAggregateVideoData] = useState(() => {
        const saved = localStorage.getItem('aggregateVideoData');
        return saved !== null ? JSON.parse(saved) : false;
    });

    // Share-card state
    const [shareModalSale, setShareModalSale] = useState(null);
    const [isShareReady, setIsShareReady] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const shareCardRef = useRef(null);

    // Fixed-position tooltip for the "not connected to YouTube" warning icon.
    // Kept in state with viewport coords so it renders above the table and is never clipped by overflow.
    const [ytWarningTooltip, setYtWarningTooltip] = useState(null);

    const showYtWarningTooltip = (e, type = 'youtube') => {
        const rect = e.currentTarget.getBoundingClientRect();
        setYtWarningTooltip({ x: rect.left + rect.width / 2, y: rect.bottom + 8, type });
    };
    const hideYtWarningTooltip = () => setYtWarningTooltip(null);

    // Add this function to get all unique funnel steps across all offers
    const getAllFunnelSteps = useMemo(() => {
        const allSteps = new Set();

        (salesData || []).forEach(resource => {
            (resource.offers || []).forEach(offer => {
                if (offer.funnel_steps && offer.funnel_steps.length > 0) {
                    offer.funnel_steps.forEach(step => {
                        allSteps.add(step.step_name);
                    });
                }
            });
        });

        return Array.from(allSteps).sort();
    }, [salesData]);

    const applyAggregateVideoData = (newValue) => {
        setAggregateVideoData(newValue);
        localStorage.setItem('aggregateVideoData', JSON.stringify(newValue));
    };

    const handleShare = useCallback((e, sale) => {
        e.stopPropagation();
        setIsShareReady(false);
        setShareModalSale(sale);
    }, []);

    const handleCloseShareModal = useCallback(() => {
        setShareModalSale(null);
        setIsShareReady(false);
        setIsDownloading(false);
    }, []);

    const handleDownload = useCallback(async () => {
        if (!shareCardRef.current || isDownloading) return;
        setIsDownloading(true);
        try {
            const canvas = await html2canvas(shareCardRef.current, {
                useCORS: true,
                allowTaint: false,
                scale: 2,
                backgroundColor: null,
                logging: false,
                imageTimeout: 15000,
            });
            const link = document.createElement('a');
            const rawTitle = shareModalSale.youtube_title || shareModalSale.name || 'share';
            link.download = `${rawTitle.substring(0, 60).replace(/[^a-z0-9]/gi, '_').toLowerCase()}.png`;
            link.href = canvas.toDataURL('image/png');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            console.error('Share card download failed:', err);
        } finally {
            setIsDownloading(false);
        }
    }, [shareModalSale, isDownloading]);

    // Get all unique tags from the data
    const allTags = useMemo(() => {
        const tagSet = new Set();
        (salesData || []).forEach(sale => {
            if (sale.link_tags && sale.link_tags.length > 0) {
                sale.link_tags.forEach(tag => {
                    const trimmedTag = tag.trim();
                    if (trimmedTag) {
                        tagSet.add(trimmedTag);
                    }
                });
            }
        });
        return Array.from(tagSet).sort();
    }, [salesData]);

    const [visibleColumns, setVisibleColumns] = useState(() => readLocalColumnPrefs().visible);
    const [columnOrder, setColumnOrder] = useState(() => readLocalColumnPrefs().order);
    const [isColumnSelectorOpen, setIsColumnSelectorOpen] = useState(false);

    const DELETE_REQUIRED_TEXT = 'delete';
    const DELETE_ALL_REQUIRED_TEXT = 'permanently delete all data';

    const { username, youtubeName, setYoutubeName, youtubeThumbUrl } = useRevit();
    const { getToken } = useAuth();

    const currencySymbol = getCurrencySymbol(currency);

    const visibleColumnsRef = useRef(visibleColumns);
    const columnOrderRef = useRef(columnOrder);
    const usernameRef = useRef(username);
    const getTokenRef = useRef(getToken);
    const columnPrefsDirty = useRef(false);
    const pendingPrefs = useRef(null);
    const saveTimer = useRef(null);
    const draggingColumnKeyRef = useRef(null);
    const dragOverRowRef = useRef(null);

    visibleColumnsRef.current = visibleColumns;
    columnOrderRef.current = columnOrder;
    usernameRef.current = username;
    getTokenRef.current = getToken;

    const fullOrder = useMemo(
        () => mergeColumnOrder(columnOrder, buildDefaultOrder(getAllFunnelSteps)),
        [columnOrder, getAllFunnelSteps]
    );
    const fullOrderRef = useRef(fullOrder);
    fullOrderRef.current = fullOrder;

    const visibleOrderedColumns = useMemo(
        () => fullOrder.filter((key) => isColumnVisible(key, visibleColumns)),
        [fullOrder, visibleColumns]
    );

    const persistColumnPrefs = useCallback(async (prefs) => {
        const user = usernameRef.current;
        if (!prefs || !user) return;
        try {
            const token = await getTokenRef.current();
            await fetch(`https://${SERVER_URL}/api/dashboard-columns/${encodeURIComponent(user)}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(prefs),
                keepalive: true,
            });
        } catch (error) {
            console.error('Error saving column order:', error);
        }
    }, []);

    const scheduleColumnSave = useCallback((order, visible) => {
        columnPrefsDirty.current = true;
        const prefs = { order, visible, updatedAt: Date.now() };
        pendingPrefs.current = prefs;
        localStorage.setItem('dashboardColumnOrder', JSON.stringify(order));
        localStorage.setItem('dashboardVisibleColumns', JSON.stringify(visible));
        localStorage.setItem('dashboardColumnPrefsUpdatedAt', String(prefs.updatedAt));
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => {
            saveTimer.current = null;
            const latest = pendingPrefs.current;
            pendingPrefs.current = null;
            persistColumnPrefs(latest);
        }, COLUMN_SAVE_DELAY_MS);
    }, [persistColumnPrefs]);

    useEffect(() => {
        if (!username) return undefined;
        let cancelled = false;

        (async () => {
            try {
                const token = await getTokenRef.current();
                const response = await fetch(
                    `https://${SERVER_URL}/api/dashboard-columns/${encodeURIComponent(username)}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                if (!response.ok || cancelled || columnPrefsDirty.current) return;
                const data = await response.json();
                if (cancelled || columnPrefsDirty.current) return;
                let prefs = data?.prefs;
                if (typeof prefs === 'string') {
                    try {
                        prefs = JSON.parse(prefs);
                    } catch (error) {
                        prefs = null;
                    }
                }
                if (!prefs || !Array.isArray(prefs.order) || !prefs.visible || typeof prefs.visible !== 'object') {
                    return;
                }
                const localUpdatedAt = Number(localStorage.getItem('dashboardColumnPrefsUpdatedAt') || 0);
                const serverUpdatedAt = Number(prefs.updatedAt) || 0;
                if (localUpdatedAt > serverUpdatedAt) {
                    scheduleColumnSave(columnOrderRef.current, visibleColumnsRef.current);
                    return;
                }
                const visible = normalizeVisibleColumns(prefs.visible);
                setVisibleColumns(visible);
                setColumnOrder(prefs.order);
                localStorage.setItem('dashboardVisibleColumns', JSON.stringify(visible));
                localStorage.setItem('dashboardColumnOrder', JSON.stringify(prefs.order));
                localStorage.setItem('dashboardColumnPrefsUpdatedAt', String(serverUpdatedAt));
            } catch (error) {
                console.error('Error loading column order:', error);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [username, scheduleColumnSave]);

    useEffect(() => {
        const flushPendingColumnSave = () => {
            if (!saveTimer.current || !pendingPrefs.current) return;
            clearTimeout(saveTimer.current);
            saveTimer.current = null;
            const latest = pendingPrefs.current;
            pendingPrefs.current = null;
            persistColumnPrefs(latest);
        };
        window.addEventListener('beforeunload', flushPendingColumnSave);
        return () => {
            window.removeEventListener('beforeunload', flushPendingColumnSave);
            flushPendingColumnSave();
        };
    }, [persistColumnPrefs]);

    const fetchSalesData = async (username) => {
        const token = await getToken();
        try {
            const response = await fetch(`https://${SERVER_URL}/api/sales/${username}`, { 
                headers: {
                'Authorization': `Bearer ${token}`,
            },
            withCredentials: true });
            if (!response.ok) {
                throw new Error('Failed to fetch sales data');
            }
            const data = await response.json();
            console.log('Dashboard sales data:', data);

            // Convert click_count and sale_count to numbers; drop rows with null/NA source
            const formattedData = data
                .filter(hasValidSource)
                .map(resource => ({
                    ...resource,
                    offers: resource.offers.map(offer => ({
                        ...offer,
                        click_count: Number(offer.click_count),
                        sale_count: Number(offer.sale_count),
                        sales_value: Number(offer.sales_value),
                        call_booking_count: Number(offer.call_booking_count)
                    }))
                }));

            return formattedData
        } catch (error) {
            console.error('Error fetching sales data:', error);
            return { resources: [] };
        }
    };

    // Function to load offers with caching
    useEffect(() => {
        const loadOffers = async () => {
            const token = await getToken();
            if (username) {
                const cachedData = offersCache[username];
                const currentTime = new Date().getTime();

                // Check if cached data exists and is not expired
                if (cachedData && (currentTime - cachedData.timestamp < CACHE_EXPIRATION_TIME)) {
                    setOffers(cachedData.data.offers);
                    setCurrency(cachedData.data.currency);
                } else {
                    try {
                        const result = await fetchOffers(username, token);
                        setOffers(result.offers);
                        setCurrency(result.currency);
                        // Cache the offers with the current timestamp
                        setOffersCache(prevCache => ({
                            ...prevCache,
                            [username]: {
                                data: result,
                                timestamp: currentTime
                            }
                        }));
                    } catch (error) {
                        console.error('Error loading offers:', error);
                    }
                }
            }
        };

        loadOffers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [username, offersCache]);

    useEffect(() => {
        const loadSalesData = async () => {
            if (username) {
                const cachedData = salesCache[username];
                const currentTime = new Date().getTime();

                // Check if cached data exists and is not expired
                if (cachedData && (currentTime - cachedData.timestamp < CACHE_EXPIRATION_TIME)) {
                    setSalesData(cachedData.data);
                } else {
                    try {
                        setIsLoading(true); // Show loading screen
                        const userSalesData = await fetchSalesData(username);
                        setSalesData(userSalesData);
                        // Cache the sales data with the current timestamp
                        setSalesCache(prevCache => ({
                            ...prevCache,
                            [username]: {
                                data: userSalesData,
                                timestamp: currentTime
                            }
                        }));
                    } catch (error) {
                        console.error('Error loading sales data:', error);
                    } finally {
                        setIsLoading(false); // Hide loading screen
                    }
                }
            }
        };

        loadSalesData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [username, salesCache]);

    const handleLogout = async () => {
        const token = await getToken();
        setLoggingOutYt(true);
        try {
            const result = await logoutFromYt(username, token);
            if (result) {
                setYoutubeName(null);
            }
        } catch (error) {
            console.error('Error during logout:', error);
        } finally {
            setLoggingOutYt(false);
        }
    };

    const refreshYouTubeData = async () => {
        // Check if user is logged in with YouTube
        if (!youtubeName) {
            alert('Please log in with your youtube channel at the top of the page to use this feature');
            return;
        }

        const token = await getToken();
        setIsRefreshing(true);
        try {
            const response = await axios.post(
                `https://${SERVER_URL}/api/refresh-yt-data/${username}`,
                {}, // empty body object
                {  // config object with headers
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );
            alert(response.data.message);

            // Refresh the data in the UI while maintaining current sorting
            const data = await fetchSalesData(username);
            setSalesData(data);

            // Clear the cache to force fresh data on next load
            setSalesCache(prevCache => {
                const newCache = { ...prevCache };
                delete newCache[username];
                return newCache;
            });
            
            // Maintain the current sort configuration - no need to change sortConfig
            // The sortedSales useMemo will automatically re-run with the new data
            
        } catch (error) {
            console.error('Error refreshing YouTube data:', error);
            const status = error.response?.status;
            const responseText = typeof error.response?.data === 'string'
                ? error.response.data
                : error.response?.data?.error || '';
            const timedOut = status === 504
                || status === 408
                || error.code === 'ECONNABORTED'
                || error.code === 'ETIMEDOUT'
                || /timeout|timed out|FUNCTION_INVOCATION_TIMEOUT/i.test(`${error.message || ''} ${responseText}`);
            alert(timedOut
                ? 'Request timed out due to a large number of videos. Run the request again to update the rest of the videos. If none were updated, ensure you are logged into your channel.'
                : 'Error refreshing YouTube data');
        } finally {
            setIsRefreshing(false);
        }
    };

    // Add data aggregation logic BEFORE filteredSales
    const aggregatedSalesData = useMemo(() => {
        const sourcedSalesData = (salesData || []).filter(hasValidSource);

        if (!aggregateVideoData) {
            return sourcedSalesData;
        }

        const aggregatedData = [];
        const processedResourceIds = new Set();
        const videos = sourcedSalesData.filter(resource => resource.category === 'video');

        videos.forEach(video => {
            const companions = sourcedSalesData.filter(resource => (
                (resource.category === 'pinComment' || resource.category === 'qr')
                && getYoutubeVideoId(resource) === video.name
            ));
            const groupedResources = [video, ...companions];

            groupedResources.forEach(resource => processedResourceIds.add(resource.id));

            if (companions.length === 0) {
                aggregatedData.push(video);
                return;
            }

            aggregatedData.push({
                ...video,
                id: `combined_${groupedResources.map(resource => resource.id).join('_')}`,
                category: 'video',
                offers: groupedResources.flatMap(resource => resource.offers || []),
                views: video.views || 0,
                sourceBreakdown: groupedResources.map(resource => ({
                    label: resource.category === 'video'
                        ? 'Video'
                        : resource.category === 'pinComment'
                            ? 'Pinned Comment'
                            : 'QR Code',
                    resource
                }))
            });
        });

        sourcedSalesData.forEach(resource => {
            if (processedResourceIds.has(resource.id)) return;

            if (resource.category === 'pinComment' || (resource.category === 'qr' && getYoutubeVideoId(resource))) {
                aggregatedData.push({
                    ...resource,
                    category: 'video'
                });
            } else {
                aggregatedData.push(resource);
            }
        });

        return aggregatedData;
    }, [salesData, aggregateVideoData]);

    // Update the filteredSales useMemo to include funnel step data
    const filteredSales = useMemo(() => {
        return aggregatedSalesData.map((resource) => {
            let totalClicks = 0;
            let totalSales = 0;
            let totalSignups = 0;
            let totalCallBookings = 0;
            let totalRevenue = 0;
            let totalFunnelStepRevenue = 0;
            const funnelStepCounts = {};
            getAllFunnelSteps.forEach(stepName => {
                funnelStepCounts[stepName] = 0;
            });

            (resource.offers || []).forEach((offer) => {
                if (selectedOffer === 'all' || offer.offer_name === selectedOffer) {
                    totalClicks += Number(offer.click_count) || 0;
                    
                    // Separate sales and signups based on offer type
                    if (offer.offer_type === 'signup') {
                        totalSignups += Number(offer.sale_count) || 0;
                    } else {
                        totalSales += Number(offer.sale_count) || 0;
                        totalRevenue += Number(offer.sales_value) || 0;
                    }

                    totalSignups += Number(offer.email_signup_count) || 0;
                    
                    totalCallBookings += Number(offer.call_booking_count) || 0;
                    
                    // Add funnel step data
                    if (offer.funnel_steps && offer.funnel_steps.length > 0) {
                        offer.funnel_steps.forEach(step => {
                            totalFunnelStepRevenue += Number(step.conversion_value) || 0;
                            funnelStepCounts[step.step_name] = (funnelStepCounts[step.step_name] || 0) + 
                                Number(step.conversion_count) || 0;
                        });
                    }
                }
            });

            return {
                ...resource,
                totalClicks,
                totalSales,
                totalSignups,
                totalCallBookings,
                totalRevenue: totalRevenue + totalFunnelStepRevenue,
                funnelStepCounts
            };
        }).filter((resource) => {
            // Base filtering by clicks and category
            const passesClickFilter = (resource.totalClicks > 0 || resource.totalSales > 0 || resource.totalSignups > 0 || resource.totalCallBookings > 0 || resource.totalRevenue > 0 || Object.values(resource.funnelStepCounts).some(count => count > 0)) &&
                                    (filterType === 'all' || resource.category === filterType);
            
            // Additional filtering by published date if that filter is active
            if (isPublishedDateFiltered && filterMode === 'published') {
                if (resource.category !== 'video' || !resource.published_at) {
                    return false;
                }
                
                const publishedDate = new Date(resource.published_at);
                return publishedDate >= publishedStartDate && 
                       publishedDate <= publishedEndDate && 
                       passesClickFilter;
            }
            
            return passesClickFilter;
        });
    }, [aggregatedSalesData, selectedOffer, filterType, isPublishedDateFiltered, filterMode, publishedStartDate, publishedEndDate, getAllFunnelSteps]);

    // Add data filtering logic
    const dataFilteredSales = useMemo(() => {
        let filtered = filteredSales;
        
        // Apply tag filter first
        if (selectedTags.length > 0) {
            filtered = filtered.filter(sale => {
                if (!sale.link_tags) return false;
                const saleTags = sale.link_tags.map(tag => tag.trim());
                return selectedTags.some(selectedTag => saleTags.includes(selectedTag));
            });
        }
        
        // Apply data filter
        if (!dataFilter.column || dataFilter.value === '') {
            return filtered;
        }

        const filterValue = parseFloat(dataFilter.value);
        if (isNaN(filterValue)) return filtered;

        return filtered.filter(sale => {
            let columnValue;
            
            switch (dataFilter.column) {
                case 'views':
                    columnValue = Number(sale.views) || 0;
                    break;
                case 'engagedViews':
                    columnValue = Number(sale.engaged_views) || 0;
                    break;
                case 'clicks':
                    columnValue = Number(sale.totalClicks) || 0;
                    break;
                case 'sales':
                    columnValue = Number(sale.totalSales) || 0;
                    break;
                case 'emails':
                    columnValue = Number(sale.totalSignups) || 0;
                    break;
                case 'revenue':
                    columnValue = Number(sale.totalRevenue) || 0;
                    break;
                case 'callBookings':
                    columnValue = Number(sale.totalCallBookings) || 0;
                    break;
                case 'clickPercentage':
                    columnValue = sale.views > 0 ? (sale.totalClicks / sale.views) * 100 : 0;
                    break;
                case 'salesPercentage':
                    columnValue = sale.totalClicks > 0 ? (sale.totalSales / sale.totalClicks) * 100 : 0;
                    break;
                case 'emailsFromViewsPercentage':
                    columnValue = sale.views > 0 ? (sale.totalSignups / sale.views) * 100 : 0;
                    break;
                case 'first_event':
                    // For date filtering, convert to timestamp for comparison
                    if (sale.first_event) {
                        columnValue = new Date(sale.first_event).getTime();
                        // Convert filter value to days ago
                        const daysAgo = parseInt(dataFilter.value);
                        if (!isNaN(daysAgo)) {
                            const compareDate = new Date();
                            compareDate.setDate(compareDate.getDate() - daysAgo);
                            const compareValue = compareDate.getTime();
                            
                            // Special handling for date comparison
                            return dataFilter.operator === 'above' 
                                ? columnValue >= compareValue  // More recent than X days ago
                                : columnValue <= compareValue; // Older than X days ago
                        }
                        return true;
                    }
                    return false; // No first event, filter it out
                default:
                    return true;
            }

            return dataFilter.operator === 'above' ? columnValue >= filterValue : columnValue <= filterValue;
        });
    }, [filteredSales, dataFilter, selectedTags]);

    const sortedSales = React.useMemo(() => {
        let sortableSales = [...dataFilteredSales];
        if (sortConfig.key !== null) {
            sortableSales.sort((a, b) => {
                let aValue, bValue;

                if (sortConfig.key === 'salesPercentage') {
                    aValue = a.totalClicks ? (a.totalSales / a.totalClicks) * 100 : 0;
                    bValue = b.totalClicks ? (b.totalSales / b.totalClicks) * 100 : 0;
                } else if (sortConfig.key === 'emailsPercentage') {
                    aValue = a.totalClicks ? (a.totalSignups / a.totalClicks) * 100 : 0;
                    bValue = b.totalClicks ? (b.totalSignups / b.totalClicks) * 100 : 0;
                } else if (sortConfig.key === 'callBookingsPercentage') {
                    aValue = a.totalClicks ? (a.totalCallBookings / a.totalClicks) * 100 : 0;
                    bValue = b.totalClicks ? (b.totalCallBookings / b.totalClicks) * 100 : 0;
                } else if (sortConfig.key === 'clickPercentage') {
                    aValue = a.views ? (a.totalClicks / a.views) * 100 : 0;
                    bValue = b.views ? (b.totalClicks / b.views) * 100 : 0;
                } else if (sortConfig.key === 'conversionPercentageFromViews') {
                    aValue = a.views ? (a.totalSales / a.views) * 100 : 0;
                    bValue = b.views ? (b.totalSales / b.views) * 100 : 0;
                } else if (sortConfig.key === 'callBookingConversionPercentage') {
                    aValue = a.totalCallBookings ? (a.totalSales / a.totalCallBookings) * 100 : 0;
                    bValue = b.totalCallBookings ? (b.totalSales / b.totalCallBookings) * 100 : 0;
                } else if (sortConfig.key === 'revenuePerView') {
                    aValue = a.views ? a.totalRevenue / a.views : 0;
                    bValue = b.views ? b.totalRevenue / b.views : 0;
                } else if (sortConfig.key === 'published_at') {
                    // Convert published_at strings to Date objects for proper comparison
                    aValue = a.published_at ? new Date(a.published_at).getTime() : 0;
                    bValue = b.published_at ? new Date(b.published_at).getTime() : 0;
                } else if (sortConfig.key === 'first_event') {
                    // Convert first_event strings to Date objects for proper comparison
                    aValue = a.first_event ? new Date(a.first_event).getTime() : Number.MAX_SAFE_INTEGER;
                    bValue = b.first_event ? new Date(b.first_event).getTime() : Number.MAX_SAFE_INTEGER;
                } else if (sortConfig.key === 'emails') {
                    // Add specific handling for emails sorting
                    aValue = Number(a.totalSignups) || 0;
                    bValue = Number(b.totalSignups) || 0;
                } else if (sortConfig.key === 'emailsFromViewsPercentage') {
                    // Add specific handling for emails from views percentage sorting
                    aValue = a.views > 0 ? (Number(a.totalSignups) / Number(a.views)) * 100 : 0;
                    bValue = b.views > 0 ? (Number(b.totalSignups) / Number(b.views)) * 100 : 0;
                } else if (sortConfig.key.startsWith('funnelStepPercentage_')) {
                    // Handle funnel step conversion-rate (% from clicks) sorting
                    const stepName = sortConfig.key.replace('funnelStepPercentage_', '');
                    const aCount = a.funnelStepCounts && a.funnelStepCounts[stepName] ? Number(a.funnelStepCounts[stepName]) : 0;
                    const bCount = b.funnelStepCounts && b.funnelStepCounts[stepName] ? Number(b.funnelStepCounts[stepName]) : 0;
                    aValue = a.totalClicks > 0 ? (aCount / a.totalClicks) * 100 : 0;
                    bValue = b.totalClicks > 0 ? (bCount / b.totalClicks) * 100 : 0;
                } else if (sortConfig.key.startsWith('funnelStep_')) {
                    // Handle funnel step sorting
                    const stepName = sortConfig.key.replace('funnelStep_', '');
                    aValue = a.funnelStepCounts && a.funnelStepCounts[stepName] ? Number(a.funnelStepCounts[stepName]) : 0;
                    bValue = b.funnelStepCounts && b.funnelStepCounts[stepName] ? Number(b.funnelStepCounts[stepName]) : 0;
                } else {
                    aValue = Number(a[sortConfig.key]) || 0;
                    bValue = Number(b[sortConfig.key]) || 0;
                }

                if (aValue < bValue) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableSales;
    }, [dataFilteredSales, sortConfig]);

    const requestSort = (key) => {
        let direction = 'descending';
        if (sortConfig.key === key && sortConfig.direction === 'descending') {
            direction = 'ascending';
        }
        setSortConfig({ key, direction });
    };

    const toggleRow = (index) => {
        const sale = currentItems[index];
        
        setToggledRows(prevState => {
            const newState = {
                ...prevState,
                [index]: !prevState[index]
            };
            
            // If row is being expanded, fetch resource data
            if (newState[index] && sale) {
                fetchResourceData(sale.id);
            }
            
            return newState;
        });
    };

    const fetchResourceData = async (resourceId) => {
        try {
            const token = await getToken();
            
            // Check if this is a combined resource
            if (typeof resourceId === 'string' && resourceId.startsWith('combined_')) {
                const resourceIds = resourceId.split('_').slice(1);

                // Fetch data for every resource grouped under this video.
                const responses = await Promise.all(resourceIds.map(id => (
                    fetch(`https://${SERVER_URL}/api/get-data-for-resource/${username}/${id}`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    })
                )));

                if (responses.every(response => response.ok)) {
                    const resourceResults = await Promise.all(responses.map(response => response.json()));
                    const primaryResourceData = resourceResults[0];

                    const mergedData = {
                        resource: primaryResourceData.resource,
                        clicks: resourceResults.flatMap(data => data.clicks || []),
                        sales: resourceResults.flatMap(data => data.sales || []),
                        signups: resourceResults.flatMap(data => data.signups || []),
                        call_bookings: resourceResults.flatMap(data => data.call_bookings || []),
                        currency: primaryResourceData.currency
                    };
                    
                    setResourceData(prev => ({
                        ...prev,
                        [resourceId]: mergedData
                    }));
                }
            } else {
                // Normal single resource fetch
                const response = await fetch(`https://${SERVER_URL}/api/get-data-for-resource/${username}/${resourceId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                
                if (response.ok) {
                    const data = await response.json();
                    setResourceData(prev => ({
                        ...prev,
                        [resourceId]: data
                    }));
                }
            }
        } catch (error) {
            console.error('Error fetching resource data:', error);
        }
    };

    const handleDateFilterSubmit = async () => {
        const token = await getToken();
        if (startDate && endDate) {
            // Pin the picker values to the local edges of their days so the
            // backend's inclusive `timestamp >= start AND <= end` filter
            // covers every event on the picked calendar days. The raw
            // `Date` values from react-datepicker sit at local midnight,
            // and once `toISOString()` shifts them to UTC the upper bound
            // silently drops roughly the last 24h of the period for any
            // user east of UTC.
            const start = new Date(startDate);
            start.setHours(0, 0, 0, 0);
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);

            setIsLoading(true);
            try {
                const data = await fetchDataByDateRange(username, start, end, token);
                // Guard against the backend returning an error envelope
                // (e.g. { error: '...' } on 5xx). Without this the next
                // render crashes the moment any `salesData.forEach` runs.
                if (Array.isArray(data)) {
                    setSalesData(data.filter(hasValidSource));
                    setIsDateFiltered(true);
                } else {
                    console.error('Date-filter endpoint returned a non-array response:', data);
                    alert('Failed to load data for the selected range. Please try again.');
                }
            } catch (error) {
                console.error('Error fetching data by date range:', error);
            } finally {
                setIsLoading(false);
            }
        }
    };

    const resetDateFilter = async () => {
        setStartDate(null);
        setEndDate(null);
        setIsDateFiltered(false);
        setIsLoading(true);
        try {
            const data = await fetchSalesData(username);
            if (Array.isArray(data)) {
                setSalesData(data);
            } else {
                console.error('fetchSalesData returned a non-array response:', data);
            }
        } catch (error) {
            console.error('Error resetting date filter:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const filteredAndSortedSales = useMemo(() => {
        // Trim the search term to handle accidental spaces
        const lowercasedSearchTerm = (searchTerm || '').toLowerCase().trim();

        // If search term is empty, return all sales
        if (!lowercasedSearchTerm) {
            return sortedSales;
        }

        const filteredSales = sortedSales.filter(sale => {
            const saleName = sale.name.toLowerCase();
            const youtubeTitle = sale.youtube_title ? sale.youtube_title.toLowerCase() : '';
            const youtubeVideoId = getYoutubeVideoId(sale)?.toLowerCase() || '';
            const tagMatches = Array.isArray(sale.link_tags)
                && sale.link_tags.some(tag => String(tag || '').toLowerCase().trim().includes(lowercasedSearchTerm));
            return saleName.includes(lowercasedSearchTerm)
                || youtubeTitle.includes(lowercasedSearchTerm)
                || youtubeVideoId.includes(lowercasedSearchTerm)
                || tagMatches;
        });

        return filteredSales;
    }, [sortedSales, searchTerm]);

    const handleDelete = (resourceId) => {
        setResourceToDelete(resourceId);
        setShowDeleteConfirmation(true);
    };

    const confirmDelete = async () => {
        if (!resourceToDelete) return;
        const token = await getToken();
        setIsDeleting(true);

        try {
            // Combined rows (video + pinComment with the same name) carry a
            // synthetic id like `combined_<videoId>_<pinCommentId>`. The
            // backend expects integer ids, so split the combined id and
            // delete both underlying resources — same approach used for
            // fetching the combined data above.
            const isCombined = typeof resourceToDelete === 'string'
                && resourceToDelete.startsWith('combined_');
            const idsToDelete = isCombined
                ? resourceToDelete.split('_').slice(1) // drop the "combined" prefix
                : [resourceToDelete];

            const results = await Promise.all(idsToDelete.map((id) =>
                fetch(`https://${SERVER_URL}/api/delete-resource/${username}/${id}`, {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    }
                })
            ));

            if (results.every((r) => r.ok)) {
                alert('Resource deleted successfully');
                setSalesData((prevSalesData) => prevSalesData.filter(sale => sale.id !== resourceToDelete));
            } else {
                alert('Failed to delete resource');
            }
        } catch (error) {
            console.error('Error deleting resource:', error);
            alert('Error deleting resource');
        } finally {
            setIsDeleting(false);
            setShowDeleteConfirmation(false);
            setResourceToDelete(null);
        }
    };

    const handleResetAllData = async () => {
        setIsDeletingData(true)
        const token = await getToken();
        setSalesData([]);
        try {
            const response = await fetch(`https://${SERVER_URL}/api/delete-all-data/${username}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            });

            if (response.ok) {
                alert('All data deleted successfully');
                setSalesData([]);
            } else {
                alert('Failed to delete all data');
            }
        } catch (error) {
            console.error('Error deleting all data:', error);
            alert('Error deleting all data');
        } finally {
            setIsDeletingData(false)
        }
        setShowConfirmation(false)
    };

    const handleUpdatePublishedDate = async (resourceId, publishedDate) => {
        const token = await getToken();
        try {
            const response = await fetch(`https://${SERVER_URL}/api/update-resource-published-date/${username}/${resourceId}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    published_at: new Date(publishedDate).toISOString()
                })
            });

            if (response.ok) {
                // Update the local state
                setSalesData(prevData => 
                    prevData.map(resource => 
                        resource.id === resourceId 
                            ? { ...resource, published_at: new Date(publishedDate).toISOString() }
                            : resource
                    )
                );
                setEditingPublishedDate(null);
                setTempPublishedDate('');
            } else {
                alert('Failed to update published date');
            }
        } catch (error) {
            console.error('Error updating published date:', error);
            alert('Error updating published date');
        }
    };

    const startEditingPublishedDate = (resourceId) => {
        setEditingPublishedDate(resourceId);
        setTempPublishedDate('');
    };

    const cancelEditingPublishedDate = () => {
        setEditingPublishedDate(null);
        setTempPublishedDate('');
    };

    const resourceIdForTags = (sale) => {
        if (typeof sale.id === 'string' && sale.id.startsWith('combined_') && sale.sourceBreakdown?.length) {
            const videoEntry = sale.sourceBreakdown.find((entry) => entry.resource?.category === 'video');
            return (videoEntry || sale.sourceBreakdown[0]).resource.id;
        }
        return sale.id;
    };

    const handleAddTag = async (sale, rawTag) => {
        const tag = String(rawTag || '').trim().replace(/,/g, '');
        if (!tag || savingTagId === sale.id) {
            setAddingTagId(null);
            setTagDraft('');
            return;
        }

        const existing = (sale.link_tags || []).map((item) => String(item).trim().toLowerCase());
        if (existing.includes(tag.toLowerCase())) {
            setAddingTagId(null);
            setTagDraft('');
            return;
        }

        const resourceId = resourceIdForTags(sale);
        const token = await getToken();
        setSavingTagId(sale.id);
        try {
            const response = await fetch(`https://${SERVER_URL}/api/add-resource-tag/${username}/${resourceId}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ tag })
            });

            if (!response.ok) {
                alert('Failed to add tag');
                return;
            }

            const data = await response.json();
            const tags = data.tags || [];
            const updateResource = (resource) => (
                resource.id === resourceId ? { ...resource, link_tags: tags } : resource
            );
            setSalesData((prevData) => prevData.map(updateResource));
            setSalesCache((prevCache) => {
                const cached = prevCache[username];
                if (!cached?.data) return prevCache;
                return {
                    ...prevCache,
                    [username]: {
                        ...cached,
                        data: cached.data.map(updateResource)
                    }
                };
            });
            setAddingTagId(null);
            setTagDraft('');
        } catch (error) {
            console.error('Error adding tag:', error);
            alert('Error adding tag');
        } finally {
            setSavingTagId(null);
        }
    };

    // Add this function to convert data to CSV and trigger download
    const downloadCSV = () => {
        try {
            // Helper function to properly escape CSV field values
            const escapeCSVField = (value) => {
                if (value === null || value === undefined) return '';
                
                // Convert to string if it's not already
                const str = String(value);
                
                // If the value contains commas, quotes, newlines, or # characters, wrap in quotes and escape any quotes
                if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('#')) {
                    return `"${str.replace(/"/g, '""')}"`;
                }
                
                return str;
            };
            
            const csvColumns = visibleOrderedColumns.filter((key) => columnCsvHeader(key, currencySymbol));
            const csvHeader = csvColumns.map((key) => columnCsvHeader(key, currencySymbol));
            let csvContent = csvHeader.join(",") + "\n";

            filteredAndSortedSales.forEach((sale) => {
                const rowValues = csvColumns.map((key) => columnCsvValue(key, sale, {
                    escapeCSVField,
                    isYoutubeResource,
                    getYoutubeVideoId,
                }));
                csvContent += rowValues.join(",") + "\n";
            });
            
            // Use Blob for better handling of large data
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            
            const link = document.createElement("a");
            link.setAttribute("href", url);
            link.setAttribute("download", "revtrack_data.csv");
            document.body.appendChild(link);
            link.click();
            
            // Clean up
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            
        } catch (error) {
            console.error("Error generating CSV:", error);
            alert("There was an error generating the CSV file. Please try again or contact support if the problem persists.");
        }
    };

    const resetPublishedDateFilter = () => {
        setPublishedStartDate(null);
        setPublishedEndDate(null);
        setIsPublishedDateFiltered(false);
    };

    // Add pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 20;
    
    // Calculate pagination indexes
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredAndSortedSales.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredAndSortedSales.length / itemsPerPage);
    
    const goToNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };
    
    const goToPreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const toggleColumnVisibility = (columnKey) => {
        const next = {
            ...visibleColumnsRef.current,
            [columnKey]: !isColumnVisible(columnKey, visibleColumnsRef.current),
        };
        visibleColumnsRef.current = next;
        setVisibleColumns(next);
        scheduleColumnSave(fullOrderRef.current, next);
    };

    /** Move `fromKey` so it sits directly before (default) or after `toKey`. */
    const reorderColumn = useCallback((fromKey, toKey, position = 'before') => {
        if (!fromKey || !toKey || fromKey === toKey) return;
        const current = fullOrderRef.current;
        if (!current.includes(fromKey) || !current.includes(toKey)) return;
        const next = current.filter((key) => key !== fromKey);
        const insertAt = next.indexOf(toKey) + (position === 'after' ? 1 : 0);
        next.splice(insertAt, 0, fromKey);
        columnOrderRef.current = next;
        setColumnOrder(next);
        scheduleColumnSave(next, visibleColumnsRef.current);
    }, [scheduleColumnSave]);

    const { draggingKey: draggingHeaderKey, getHeaderProps: getDraggableHeaderProps } = useColumnDragReorder({
        onReorder: reorderColumn,
        getLabel: (key) => columnLabel(key, currencySymbol),
    });

    const resetColumnVisibility = () => {
        const defaultColumns = { ...STATIC_COLUMN_DEFAULTS };
        const defaultOrder = buildDefaultOrder(getAllFunnelSteps);
        visibleColumnsRef.current = defaultColumns;
        columnOrderRef.current = defaultOrder;
        setVisibleColumns(defaultColumns);
        setColumnOrder(defaultOrder);
        scheduleColumnSave(defaultOrder, defaultColumns);
    };

    useEffect(() => {
        if (!isColumnSelectorOpen) return undefined;
        const handleClickOutside = (event) => {
            if (!event.target.closest('.column-selector-container')) {
                setIsColumnSelectorOpen(false);
            }
        };
        const handleKey = (event) => {
            if (event.key === 'Escape') setIsColumnSelectorOpen(false);
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleKey);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKey);
        };
    }, [isColumnSelectorOpen]);

    // Add a function to calculate totals for numeric columns
    const calculateTotals = useMemo(() => {
        const totals = {
            views: 0,
            engagedViews: 0,
            totalClicks: 0,
            totalSales: 0,
            totalSignups: 0,
            totalCallBookings: 0,
            totalRevenue: 0,
            // Initialize funnel step totals
            funnelStepTotals: {}
        };
        
        // Initialize funnel step totals
        getAllFunnelSteps.forEach(stepName => {
            totals.funnelStepTotals[stepName] = 0;
        });
        
        filteredAndSortedSales.forEach(sale => {
            // Pinned comments and QR resources share their video's view count.
            // Exclude them to avoid double-counting when aggregation is disabled.
            if (sale.category !== 'pinComment' && sale.category !== 'qr') {
                totals.views += Number(sale.views) || 0;
                totals.engagedViews += Number(sale.engaged_views) || 0;
            }
            totals.totalClicks += Number(sale.totalClicks) || 0;
            totals.totalCallBookings += Number(sale.totalCallBookings) || 0;
            totals.totalSales += Number(sale.totalSales) || 0;
            totals.totalSignups += Number(sale.totalSignups) || 0;
            totals.totalRevenue += Number(sale.totalRevenue) || 0;
            
            // Add funnel step totals
            getAllFunnelSteps.forEach(stepName => {
                if (sale.funnelStepCounts && sale.funnelStepCounts[stepName]) {
                    totals.funnelStepTotals[stepName] += Number(sale.funnelStepCounts[stepName]) || 0;
                }
            });
        });
        
        // Calculate percentages
        totals.clickPercentage = totals.views ? ((totals.totalClicks / totals.views) * 100).toFixed(2) : 'N/A';
        totals.callBookingsPercentage = totals.totalClicks ? ((totals.totalCallBookings / totals.totalClicks) * 100).toFixed(2) : 'N/A';
        totals.salesPercentage = totals.totalClicks ? ((totals.totalSales / totals.totalClicks) * 100).toFixed(2) : 'N/A';
        totals.signupsPercentage = totals.totalClicks ? ((totals.totalSignups / totals.totalClicks) * 100).toFixed(2) : 'N/A';
        totals.conversionPercentageFromViews = totals.views ? ((totals.totalSales / totals.views) * 100).toFixed(2) : 'N/A';
        totals.callBookingConversionPercentage = totals.totalCallBookings ? ((totals.totalSales / totals.totalCallBookings) * 100).toFixed(2) : 'N/A';
        totals.revenuePerView = totals.views ? (totals.totalRevenue / totals.views).toFixed(4) : 'N/A';
        
        // Calculate funnel step conversion-rate totals (% from clicks)
        totals.funnelStepPercentageTotals = {};
        getAllFunnelSteps.forEach(stepName => {
            totals.funnelStepPercentageTotals[stepName] = totals.totalClicks
                ? ((totals.funnelStepTotals[stepName] / totals.totalClicks) * 100).toFixed(2)
                : 'N/A';
        });
        
        return totals;
    }, [filteredAndSortedSales, getAllFunnelSteps]);

    const handleLinkResource = (resource) => {
        setLinkingResource(resource);
        setShowLinkPopup(true);
        setLinkUrl('');
    };

    const handleCreateLink = async () => {
        if (!linkUrl || !linkingResource) return;
        
        const token = await getToken();
        try {
            await axios.post(
                `https://${SERVER_URL}/api/create-link`,
                {
                    username: username,
                    utm: linkingResource.name,
                    url: linkUrl
                },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );
            
            // Update the resource in salesData to include the new link_url
            setSalesData(prevData => 
                prevData.map(sale => 
                    sale.id === linkingResource.id 
                        ? { ...sale, link_url: linkUrl }
                        : sale
                )
            );
            
            setShowLinkPopup(false);
            setLinkingResource(null);
            setLinkUrl('');
            
        } catch (error) {
            console.error('Error creating link:', error);
            alert('Error creating link');
        }
    };

    // Auto-apply date filter when both dates are set
    useEffect(() => {
        if (startDate && endDate) {
            handleDateFilterSubmit();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate]);

    // Auto-apply published date filter when both dates are set
    useEffect(() => {
        if (publishedStartDate && publishedEndDate) {
            setFilterMode('published');
            setIsPublishedDateFiltered(true);
        }
    }, [publishedStartDate, publishedEndDate]);

    // Add function to check if any filters are applied
    const hasActiveFilters = () => {
        return filterType !== 'all' || 
               selectedOffer !== 'all' || 
               isDateFiltered || 
               isPublishedDateFiltered || 
               searchTerm !== '' ||
               selectedTags.length > 0 ||
               (dataFilter.column && dataFilter.value);
    };

    // Add function to clear all filters
    const clearAllFilters = async () => {
        setFilterType('all');
        setSelectedOffer('all');
        setSearchTerm('');
        setSelectedTags([]);
        setDataFilter({column: '', operator: 'above', value: ''});
        
        // Reset date filters
        if (isDateFiltered) {
            await resetDateFilter();
        }
        if (isPublishedDateFiltered) {
            resetPublishedDateFilter();
        }
    };


    // Check if user has landing pages in localStorage
    useEffect(() => {
        const savedLandingPages = JSON.parse(localStorage.getItem('landingPages')) || [];
        setHasLandingPages(savedLandingPages.length > 0);
    }, []);

    // Process resource data for chart
    const processResourceDataForChart = (data) => {
        if (!data || !data.clicks) return [];

        const dailyData = {};
        const clicksWithSource = (data.clicks || []).filter(
            (click) => click.resource_name != null && click.resource_name !== '' && click.type != null && click.type !== ''
        );
        
        // Get all dates from the data
        const allDates = [
            ...clicksWithSource.map(click => new Date(click.timestamp).toISOString().split('T')[0]),
            ...(data.sales || []).map(sale => new Date(sale.timestamp).toISOString().split('T')[0]),
            ...(data.signups || []).map(signup => new Date(signup.timestamp).toISOString().split('T')[0]),
            ...(data.call_bookings || []).map(call => new Date(call.timestamp).toISOString().split('T')[0])
        ];
        
        if (allDates.length === 0) return [];
        
        // Find the date range from the actual data
        const sortedDates = allDates.sort();
        const startDate = new Date(sortedDates[0]);
        const endDate = new Date(sortedDates[sortedDates.length - 1]);
        
        // Initialize all dates in the range
        for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
            const dateStr = d.toISOString().split('T')[0];
            dailyData[dateStr] = {
                date: dateStr,
                clicks: 0,
                sales: 0,
                signups: 0,
                calls: 0,
                revenue: 0
            };
        }

        // Process clicks (skip any without a real source/resource)
        clicksWithSource.forEach(click => {
            const date = new Date(click.timestamp).toISOString().split('T')[0];
            if (dailyData[date]) {
                dailyData[date].clicks++;
            }
        });

        // Process sales
        if (data.sales) {
            data.sales.forEach(sale => {
                const date = new Date(sale.timestamp).toISOString().split('T')[0];
                if (dailyData[date]) {
                    dailyData[date].sales++;
                    dailyData[date].revenue += Number(sale.value) || 0;
                }
            });
        }

        // Process signups
        if (data.signups) {
            data.signups.forEach(signup => {
                const date = new Date(signup.timestamp).toISOString().split('T')[0];
                if (dailyData[date]) {
                    dailyData[date].signups++;
                }
            });
        }

        // Process call bookings
        if (data.call_bookings) {
            data.call_bookings.forEach(call => {
                const date = new Date(call.timestamp).toISOString().split('T')[0];
                if (dailyData[date]) {
                    dailyData[date].calls++;
                }
            });
        }

        return Object.values(dailyData).sort((a, b) => new Date(a.date) - new Date(b.date));
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="custom-tooltip">
                    <p className="tooltip-label">{formatDate(label)}</p>
                    {payload.map((entry, index) => (
                        <p key={index} style={{ color: entry.color }}>
                            {`${entry.name}: ${entry.value}`}
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    // Table-level actions shown at the right end of the filter rail.
    const tableActions = (
        <>
            <button
                type="button"
                role="switch"
                aria-checked={showTotals}
                className={`filter-pill filter-pill--action filter-pill--toggle${showTotals ? ' is-on' : ''}`}
                onClick={() => setShowTotals((prev) => !prev)}
            >
                <Sigma size={14} className="filter-pill__icon" aria-hidden="true" />
                Show totals
                <span className="filter-pill__switch" aria-hidden="true" />
            </button>
            <div className="filter-pill-wrap column-selector-container">
                <button
                    type="button"
                    className={`filter-pill filter-pill--action column-selector-toggle${isColumnSelectorOpen ? ' is-open' : ''}`}
                    onClick={() => setIsColumnSelectorOpen((prev) => !prev)}
                    aria-haspopup="dialog"
                    aria-expanded={isColumnSelectorOpen}
                    aria-controls={isColumnSelectorOpen ? 'dashboard-column-selector' : undefined}
                >
                    <Columns3 size={14} className="filter-pill__icon" aria-hidden="true" />
                    Customize columns
                </button>
                {isColumnSelectorOpen && (
                    <div
                        id="dashboard-column-selector"
                        role="dialog"
                        aria-label="Customize columns"
                        className="column-selector-dropdown filter-popover is-right"
                    >
                        <div className="column-selector-header">
                            <div>
                                <h4>Columns</h4>
                                <p className="column-selector-hint">Drag to reorder</p>
                            </div>
                            <button className="reset-columns-button" onClick={resetColumnVisibility}>
                                Reset
                            </button>
                        </div>
                        <div className="column-options">
                            {fullOrder.map((key) => (
                                <div
                                    key={key}
                                    className="column-option-row"
                                    onDragOver={(event) => {
                                        event.preventDefault();
                                        event.dataTransfer.dropEffect = 'move';
                                        if (dragOverRowRef.current && dragOverRowRef.current !== event.currentTarget) {
                                            dragOverRowRef.current.classList.remove('drag-over');
                                        }
                                        event.currentTarget.classList.add('drag-over');
                                        dragOverRowRef.current = event.currentTarget;
                                    }}
                                    onDrop={(event) => {
                                        event.preventDefault();
                                        const fromKey = event.dataTransfer.getData('text/plain') || draggingColumnKeyRef.current;
                                        reorderColumn(fromKey, key);
                                        draggingColumnKeyRef.current = null;
                                        event.currentTarget.classList.remove('drag-over');
                                        if (dragOverRowRef.current) {
                                            dragOverRowRef.current.classList.remove('drag-over');
                                            dragOverRowRef.current = null;
                                        }
                                    }}
                                >
                                    <span
                                        className="column-drag-handle"
                                        draggable
                                        title="Drag to reorder"
                                        aria-label={`Reorder ${columnLabel(key, currencySymbol)}`}
                                        onDragStart={(event) => {
                                            event.dataTransfer.setData('text/plain', key);
                                            event.dataTransfer.effectAllowed = 'move';
                                            draggingColumnKeyRef.current = key;
                                            event.currentTarget.closest('.column-option-row')?.classList.add('dragging');
                                        }}
                                        onDragEnd={(event) => {
                                            draggingColumnKeyRef.current = null;
                                            event.currentTarget.closest('.column-option-row')?.classList.remove('dragging');
                                            if (dragOverRowRef.current) {
                                                dragOverRowRef.current.classList.remove('drag-over');
                                                dragOverRowRef.current = null;
                                            }
                                        }}
                                    >
                                        <GripVertical size={14} />
                                    </span>
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={isColumnVisible(key, visibleColumns)}
                                            onChange={() => toggleColumnVisibility(key)}
                                        />
                                        {columnLabel(key, currencySymbol)}
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            <button type="button" className="filter-pill filter-pill--action" onClick={downloadCSV}>
                <Download size={14} className="filter-pill__icon" aria-hidden="true" />
                Download CSV
            </button>
        </>
    );

    return (
        <div className={`dashboard-container ${!isSidebarOpen ? 'sidebar-collapsed' : ''}`}>
            {isLoading && (
                <div className="loading-overlay">
                    <div className="loading-text">Loading...</div>
                </div>
            )}
            {loggingOutYt && (
                <div className="loading-overlay">
                    <div className="loading-text">Logging out...</div>
                </div>
            )}
            <div className="table-container">
                <div className="youtube-account-container">
                        {youtubeName ? (
                            <div className="youtube-account-info">
                                <p>
                                    Logged in as:
                                </p>
                                <div className="youtube-account-details">
                                    {youtubeThumbUrl && (
                                        <img 
                                            src={youtubeThumbUrl} 
                                            alt="Channel" 
                                            className="channel-thumbnail" 
                                            style={{ width: '28px', height: '28px', borderRadius: '50%', marginRight: '8px', verticalAlign: 'middle' }}
                                        />
                                    )}
                                    {youtubeName}
                                    <Icon 
                                        name="logout" 
                                        className="yt-logout-icon" 
                                        onClick={handleLogout}
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="youtube-account-info">
                                <button className="youtube-login-button" onClick={() => handleLoginWithYouTube(username)}>
                                    <Icon name="youtube" size={22} />
                                    Login with YouTube
                                </button>
                            </div>
                        )}
                    </div>

                <div className="date-range-picker-container">
                </div>
                <div className="top-actions-container">
                  <div className="notes body-tiny-regular">
                        <p><i>Note: Only sources that have at least one click/conversion will be shown here.</i></p>
                        <p><i>Is your data not showing up? Please go through the troubleshooting guide <NavLink style={{color: 'white', fontWeight: 'bold', textDecoration: 'underline'}} to="/help">here</NavLink></i></p>
                    </div>
                </div>
                <div className="search-content-container">
                    <div className="search-input-content-container">
                        <Icon name="search" size={16} className="search-icon" />
                        <input
                            type="text"
                            placeholder="Search by name, title or tag..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="search-input"
                        />
                    </div>
                    <div className="action-buttons search-row-actions">
                        <button
                            className="download-button"
                            onClick={refreshYouTubeData}
                            disabled={isRefreshing}
                        >
                            <Icon name="refresh" size={16} />
                            {isRefreshing ? 'Refreshing...' : 'Refresh YouTube Data'}
                        </button>
                        <button onClick={() => setShowConfirmation(true)} className="clear-filter-button">Reset All Data</button>
                    </div>
                </div>
                <DashboardFilterRail
                    actions={tableActions}
                    filterType={filterType}
                    onFilterTypeChange={setFilterType}
                    offers={offers}
                    selectedOffer={selectedOffer}
                    onSelectedOfferChange={setSelectedOffer}
                    startDate={startDate}
                    endDate={endDate}
                    onDateRangeChange={(start, end) => {
                        setStartDate(start);
                        setEndDate(end);
                    }}
                    onClearDateRange={() => {
                        if (isDateFiltered) {
                            resetDateFilter();
                        } else {
                            setStartDate(null);
                            setEndDate(null);
                        }
                    }}
                    publishedStartDate={publishedStartDate}
                    publishedEndDate={publishedEndDate}
                    onPublishedRangeChange={(start, end) => {
                        setPublishedStartDate(start);
                        setPublishedEndDate(end);
                    }}
                    onClearPublishedRange={resetPublishedDateFilter}
                    dataFilter={dataFilter}
                    onDataFilterChange={setDataFilter}
                    allTags={allTags}
                    selectedTags={selectedTags}
                    onSelectedTagsChange={setSelectedTags}
                    aggregateVideoData={aggregateVideoData}
                    onAggregateVideoDataChange={applyAggregateVideoData}
                    hasActiveFilters={hasActiveFilters()}
                    onClearAll={clearAllFilters}
                    resultCount={filteredAndSortedSales.length}
                />
                    <table className={draggingHeaderKey ? 'is-reordering-columns' : undefined}>
                        <colgroup>
                            <col />
                            {visibleOrderedColumns.map((key) => (
                                <col key={key} className={draggingHeaderKey === key ? 'is-dragging' : undefined} />
                            ))}
                            <col />
                        </colgroup>
                        <thead>
                            <tr>
                                <th className="row-expander-col" aria-label="Expand"></th>
                                {visibleOrderedColumns.map((key) => {
                                    const header = renderColumnHeader(key, {
                                        sortConfig,
                                        requestSort,
                                        currencySymbol,
                                    });
                                    // Headers stay plain <th>s in DashboardColumns; the drag
                                    // behaviour is layered on here so sorting keeps working.
                                    return React.cloneElement(header, getDraggableHeaderProps(key, header.props));
                                })}
                                <th className="row-actions-col" aria-label="Row actions"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {/* Add totals row if showTotals is true */}
                            {showTotals && (
                                <tr className="totals-row">
                                    <td className="row-expander-col"></td>
                                    {visibleOrderedColumns.map((key) => renderColumnTotal(key, {
                                        calculateTotals,
                                        currencySymbol,
                                    }))}
                                    <td className="row-actions-col"></td>
                                </tr>
                            )}
                            
                            {currentItems.map((sale, index) => {
                                const clickPercentage = sale.views ? ((sale.totalClicks / sale.views) * 100).toFixed(2) : 'N/A';
                                const callBookingsPercentage = sale.totalClicks ? ((sale.totalCallBookings / sale.totalClicks) * 100).toFixed(2) : 'N/A';
                                const salesPercentage = sale.totalClicks ? ((sale.totalSales / sale.totalClicks) * 100).toFixed(2) : 'N/A';
                                const signupsPercentage = sale.totalClicks ? ((sale.totalSignups / sale.totalClicks) * 100).toFixed(2) : 'N/A';
                                const formattedViews = sale.views ? new Intl.NumberFormat().format(sale.views) : 'N/A';
                                const formattedEngagedViews = sale.engaged_views ? new Intl.NumberFormat().format(sale.engaged_views) : 'N/A';
                                const formattedPublishedDate = sale.published_at 
                                    ? new Date(sale.published_at).toLocaleDateString() 
                                    : '';
                                
                                // Rows expand into a per-offer breakdown only when no single offer is selected.
                                const isExpandable = selectedOffer === 'all';
                                const isExpanded = isExpandable && Boolean(toggledRows[index]);
                                const rowClassName = [
                                    'clickable-row',
                                    isExpandable ? 'is-expandable' : '',
                                    isExpanded ? 'is-expanded' : '',
                                ].filter(Boolean).join(' ');

                                return (
                                    <React.Fragment key={index}>
                                        <tr
                                            className={rowClassName}
                                            onClick={isExpandable ? () => toggleRow(index) : undefined}
                                            aria-expanded={isExpandable ? isExpanded : undefined}
                                        >
                                            <td className="row-expander-col">
                                                {isExpandable && (
                                                    <span
                                                        className="row-expander"
                                                        title={isExpanded ? 'Hide breakdown by offer' : 'Show breakdown by offer'}
                                                        aria-hidden="true"
                                                    >
                                                        <ChevronRight size={14} strokeWidth={2.25} />
                                                    </span>
                                                )}
                                            </td>
                                            {visibleOrderedColumns.map((key) => renderColumnCell(key, sale, index, {
                                                selectedOffer,
                                                currencySymbol,
                                                editingPublishedDate,
                                                tempPublishedDate,
                                                setTempPublishedDate,
                                                handleUpdatePublishedDate,
                                                cancelEditingPublishedDate,
                                                startEditingPublishedDate,
                                                isYoutubeResource,
                                                getYoutubeVideoId,
                                                showYtWarningTooltip,
                                                hideYtWarningTooltip,
                                                handleLinkResource,
                                                addingTagId,
                                                tagDraft,
                                                savingTagId,
                                                setTagDraft,
                                                setAddingTagId,
                                                handleAddTag,
                                                tagEditCancelled,
                                                clickPercentage,
                                                callBookingsPercentage,
                                                salesPercentage,
                                                signupsPercentage,
                                                formattedViews,
                                                formattedEngagedViews,
                                                formattedPublishedDate,
                                            }))}
                                            <td className="row-actions-col">
                                                <div className="row-actions">
                                                    <button
                                                        type="button"
                                                        onClick={(e) => handleShare(e, sale)}
                                                        className="row-action-button"
                                                        title="Share as image"
                                                        aria-label="Share as image"
                                                    >
                                                        <Share2 size={12} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => { e.stopPropagation(); handleDelete(sale.id); }}
                                                        className="row-action-button row-action-button--danger"
                                                        title="Delete resource"
                                                        aria-label="Delete resource"
                                                    >
                                                        <Trash2 size={12} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                        {isExpanded && (
                                            <tr className="expanded-row">
                                                <td colSpan={visibleOrderedColumns.length + 2}>
                                                    <div className="expanded-content">
                                                        {/* Show breakdown for combined video resources */}
                                                        {sale.sourceBreakdown && (
                                                            <div className="combined-breakdown">
                                                                <h4>Breakdown by Source:</h4>
                                                                {sale.sourceBreakdown.map(({ label, resource }) => (
                                                                    <div className="breakdown-section" key={resource.id}>
                                                                        <h5>{label}:</h5>
                                                                        <table className="nested-table">
                                                                            <thead>
                                                                                <tr>
                                                                                    <th>Offer Name</th>
                                                                                    <th>Clicks</th>
                                                                                    <th>Call Bookings</th>
                                                                                    <th>Sales / Emails</th>
                                                                                    <th>Revenue</th>
                                                                                    <th>Conversion Rate</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {(resource.offers || [])
                                                                                    .filter(offer => offer.click_count > 0 || offer.sale_count > 0 || offer.call_booking_count > 0)
                                                                                    .map((offer, offerIndex) => (
                                                                                        <tr key={offerIndex}>
                                                                                            <td>{offer.offer_name}</td>
                                                                                            <td>{offer.click_count}</td>
                                                                                            <td>{offer.call_booking_count}</td>
                                                                                            <td>{offer.sale_count}</td>
                                                                                            <td>{currencySymbol}{Number(offer.sales_value || 0).toFixed(2)}</td>
                                                                                            <td>{offer.click_count ? ((offer.sale_count / offer.click_count) * 100).toFixed(2) : 'N/A'}%</td>
                                                                                        </tr>
                                                                                    ))
                                                                                }
                                                                            </tbody>
                                                                        </table>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                        
                                                        {/* Original expanded table for non-combined resources */}
                                                        {!sale.sourceBreakdown && (
                                                            <table className="nested-table">
                                                                <thead>
                                                                    <tr>
                                                                        <th>Offer Name</th>
                                                                        <th>Clicks</th>
                                                                        <th>Call Bookings</th>
                                                                    
                                                                        {/* Get all unique funnel step names across all offers */}
                                                                        {(() => {
                                                                            // Collect all unique funnel step names
                                                                            const allStepNames = new Set();
                                                                            (sale.offers || []).forEach(offer => {
                                                                                if (offer.funnel_steps && offer.funnel_steps.length > 0) {
                                                                                    offer.funnel_steps.forEach(step => {
                                                                                        allStepNames.add(step.step_name);
                                                                                    });
                                                                                }
                                                                            });
                                                                        
                                                                            // Return header cells for each unique step
                                                                            return Array.from(allStepNames).map(stepName => (
                                                                                <th key={stepName} className="funnel-step-header">{stepName}</th>
                                                                            ));
                                                                        })()}
                                                                        <th>Emails</th>
                                                                        <th>Sales</th>
                                                                        <th>Revenue</th>
                                                                        <th>Conversion Rate</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {sale.offers
                                                                        .filter(offer => 
                                                                            offer.click_count > 0 || 
                                                                            offer.sale_count > 0 || 
                                                                            offer.call_booking_count > 0 || 
                                                                            (offer.funnel_steps && offer.funnel_steps.some(step => step.conversion_count > 0))
                                                                        )
                                                                        .map((offer, offerIndex) => {
                                                                            const offerSalesPercentage = offer.click_count ? 
                                                                                ((offer.sale_count / offer.click_count) * 100).toFixed(2) : 
                                                                                'N/A';
                                                                    
                                                                            // Calculate total revenue including funnel step values
                                                                            const funnelStepRevenue = offer.funnel_steps ? 
                                                                                offer.funnel_steps.reduce((sum, step) => sum + Number(step.conversion_value || 0), 0) : 0;
                                                                            const totalRevenue = Number(offer.sales_value || 0) + funnelStepRevenue;
                                                                    
                                                                            // Get all unique funnel step names
                                                                            const allStepNames = new Set();
                                                                            (sale.offers || []).forEach(o => {
                                                                                if (o.funnel_steps && o.funnel_steps.length > 0) {
                                                                                    o.funnel_steps.forEach(step => {
                                                                                        allStepNames.add(step.step_name);
                                                                                    });
                                                                                }
                                                                            });
                                                                    
                                                                            return (
                                                                                <tr key={offerIndex}>
                                                                                    <td>{offer.offer_name}</td>
                                                                                    <td>{offer.click_count}</td>
                                                                                    <td>{offer.call_booking_count}</td>
                                                                                    {/* Add a cell for each funnel step */}
                                                                                    {Array.from(allStepNames).map(stepName => {
                                                                                        // Find the step data for this offer and step name
                                                                                        const stepData = offer.funnel_steps?.find(step => step.step_name === stepName);
                                                                                    
                                                                                        return (
                                                                                            <td key={stepName} className="funnel-step-cell">
                                                                                                {stepData ? (
                                                                                                    <div className="funnel-step-value">
                                                                                                        {stepData.conversion_count}
                                                                                                    </div>
                                                                                                ) : (
                                                                                                    <span>-</span>
                                                                                                )}
                                                                                            </td>
                                                                                        );
                                                                                    })}
                                                                                    <td>{parseInt(offer.email_signup_count) + (offer.offer_type == 'signup' ? offer.sale_count : 0)}</td>
                                                                                    <td>{offer.offer_type == 'paid' ? offer.sale_count : 0}</td>
                                                                                    <td>{currencySymbol}{totalRevenue.toFixed(2)}</td>
                                                                                    <td>{offerSalesPercentage}%</td>
                                                                                </tr>
                                                                            );
                                                                        })
                                                                    }
                                                                </tbody>
                                                            </table>
                                                        )}
                                                        
                                                        {/* Resource Chart */}
                                                        {resourceData[sale.id] && (
                                                            <div className="resource-chart-container">
                                                                <h4>Performance Over Time</h4>
                                                                <div className="resource-chart">
                                                                    <ResponsiveContainer width="100%" height={300}>
                                                                        <AreaChart data={processResourceDataForChart(resourceData[sale.id])}>
                                                                            <CartesianGrid strokeDasharray="0" horizontal={true} vertical={false} />
                                                                            <XAxis dataKey="date" tickFormatter={formatDate} />
                                                                            <YAxis />
                                                                            <Tooltip content={<CustomTooltip />} />
                                                                            <Legend />
                                                                            <Area 
                                                                                type="linear"
                                                                                dataKey="clicks"
                                                                                name="Clicks"
                                                                                stroke="#00F2A5"
                                                                                strokeWidth={2}
                                                                                fill="#00F2A5"
                                                                                fillOpacity={0.3}
                                                                            />
                                                                            <Area 
                                                                                type="linear"
                                                                                dataKey="calls"
                                                                                name="Calls"
                                                                                stroke="#FF6B6B"
                                                                                strokeWidth={2}
                                                                                fill="#FF6B6B"
                                                                                fillOpacity={0.3}
                                                                            />
                                                                            <Area 
                                                                                type="linear"
                                                                                dataKey="sales"
                                                                                name="Sales"
                                                                                stroke="#A149D5"
                                                                                strokeWidth={2}
                                                                                fill="#A149D5"
                                                                                fillOpacity={0.3}
                                                                            />
                                                                            <Area 
                                                                                type="linear"
                                                                                dataKey="signups"
                                                                                name="Emails"
                                                                                stroke="#FFD700"
                                                                                strokeWidth={2}
                                                                                fill="#FFD700"
                                                                                fillOpacity={0.3}
                                                                            />
                                                                        </AreaChart>
                                                                    </ResponsiveContainer>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                            
                            {/* Empty state messages */}
                            {currentItems.length === 0 && !isLoading && (
                                <tr className="empty-state-row">
                                    <td colSpan="20" style={{ textAlign: 'center', padding: '60px 20px' }}>
                                        <div className="empty-state">
                                            {salesData.length === 0 && offers.length === 0 ? (
                                                // No data at all - first time user
                                                <div className="empty-state-content">
                                                    <div className="empty-state-icon">📊</div>
                                                    <h3 className="empty-state-title">When you start tracking, your data will appear here.</h3>
                                                    <p className="empty-state-subtitle">To get started, create your first offer here</p>
                                                    <a href="/code" className="empty-state-button">
                                                        Go to Code and Offers →
                                                    </a>
                                                </div>
                                            ) : (
                                                // Has offers but no tracking data
                                                <div className="empty-state-content">
                                                    <div className="empty-state-icon">🔗</div>
                                                    {hasLandingPages ? (
                                                        <>
                                                            <h3 className="empty-state-title">No data even after you clicked a tracking link? Troubleshoot your setup:</h3>
                                                            <a href="/help" className="empty-state-button">
                                                                Get Help →
                                                            </a>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <h3 className="empty-state-title">No data yet? Create your first tracking link to start tracking in seconds.</h3>
                                                            <a href="/links" className="empty-state-button">
                                                                Create tracking links →
                                                            </a>
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                                        {filteredAndSortedSales.length > itemsPerPage && (
                        <div className="pagination-controls">
                            <button 
                                onClick={goToPreviousPage} 
                                disabled={currentPage === 1}
                                className="pagination-button"
                            >
                                Previous
                            </button>
                            
                            <div className="pagination-info">
                                Page {currentPage} of {totalPages} 
                                <span className="pagination-summary">
                                    (Showing {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredAndSortedSales.length)} of {filteredAndSortedSales.length})
                                </span>
                            </div>
                            
                            <button 
                                onClick={goToNextPage} 
                                disabled={currentPage === totalPages}
                                className="pagination-button"
                            >
                                Next
                            </button>
                        </div>
                    )}
            </div>

            {showDeleteConfirmation && (
                <div className="confirmation-overlay">
                    <div className="confirmation-popup confirmation-popup--danger">
                        <div className="confirmation-popup__header">
                            <div className="confirmation-popup__icon">&#9888;</div>
                            <h3 className="confirmation-popup__title">Delete Resource</h3>
                        </div>
                        <p className="confirmation-popup__body">Are you sure you want to delete this resource? This operation is irreversible.</p>
                        <div className="confirmation-popup__warning">
                            All click &amp; conversion data associated with this resource will be lost forever.
                        </div>
                        <label className="confirmation-popup__label">Type <strong>"{DELETE_REQUIRED_TEXT}"</strong> to confirm:</label>
                        <input
                            type="text"
                            value={deleteConfirmationText}
                            onChange={(e) => setDeleteConfirmationText(e.target.value)}
                            placeholder={`Type "${DELETE_REQUIRED_TEXT}" to confirm`}
                            className="confirmation-popup__input"
                        />
                        <div className="confirmation-popup__actions">
                            <button 
                                onClick={() => {
                                    setShowDeleteConfirmation(false);
                                    setDeleteConfirmationText('');
                                }} 
                                className="cancel-button" 
                                disabled={isDeleting}
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={confirmDelete} 
                                className="proceed-button" 
                                disabled={deleteConfirmationText.toLowerCase() !== DELETE_REQUIRED_TEXT || isDeleting}
                            >
                                {isDeleting ? 'Deleting…' : 'Yes, Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showConfirmation && (
                <div className="confirmation-overlay">
                    <div className="confirmation-popup confirmation-popup--danger">
                        <div className="confirmation-popup__header">
                            <div className="confirmation-popup__icon">&#9888;</div>
                            <h3 className="confirmation-popup__title">Delete All Data</h3>
                        </div>
                        <p className="confirmation-popup__body">Are you sure? This will permanently delete all data.</p>
                        <div className="confirmation-popup__warning">
                            For tracking to stop, you must remove the RevTrack scripts from your website.
                        </div>
                        <label className="confirmation-popup__label">Type <strong>"{DELETE_ALL_REQUIRED_TEXT}"</strong> to confirm:</label>
                        <input
                            type="text"
                            value={confirmationText}
                            onChange={(e) => setConfirmationText(e.target.value)}
                            placeholder={`Type "${DELETE_ALL_REQUIRED_TEXT}" to confirm`}
                            className="confirmation-popup__input"
                        />
                        <div className="confirmation-popup__actions">
                            <button
                                onClick={() => setShowConfirmation(false)}
                                className="cancel-button"
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={handleResetAllData} 
                                className="proceed-button" 
                                disabled={confirmationText.toLowerCase() !== DELETE_ALL_REQUIRED_TEXT || isDeletingData}
                            >
                                {isDeletingData ? 'Deleting…' : 'Yes, Delete All Data'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {showLinkPopup && (
                <div className="confirmation-popup">
                    <h3>Add URL Link</h3>
                    <p>Add a URL link for: <strong>{linkingResource?.name}</strong></p>
                    <p>This is the link of the post/page where this link is placed (eg, Instagram/Linkedin post or website page)</p>
                    <input
                        type="url"
                        value={linkUrl}
                        onChange={(e) => setLinkUrl(e.target.value)}
                        placeholder="https://example.com"
                        className="link-url-input"
                    />
                    <div className="popup-actions">
                        <button 
                            onClick={() => setShowLinkPopup(false)}
                            className="cancel-button"
                        >
                            Cancel
                        </button>
                        <button 
                            onClick={handleCreateLink}
                            disabled={!linkUrl}
                            className={`link-button ${!linkUrl ? 'disabled' : 'active'}`}
                        >
                            Link
                        </button>
                    </div>
                </div>
            )}

            {/* Share modal */}
            {shareModalSale && (
                <div className="share-modal-overlay" onClick={handleCloseShareModal}>
                    <div className="share-modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="share-modal-x" onClick={handleCloseShareModal} title="Close">
                            <X size={16} />
                        </button>
                        <div className="share-modal-card-wrap">
                            <ShareCard
                                ref={shareCardRef}
                                sale={shareModalSale}
                                currencySymbol={currencySymbol}
                                onReady={() => setIsShareReady(true)}
                            />
                        </div>
                        <div className="share-modal-actions">
                            <button
                                className="share-modal-download-btn"
                                onClick={handleDownload}
                                disabled={!isShareReady || isDownloading}
                            >
                                <Download size={14} />
                                {isDownloading ? 'Generating…' : 'Download Image'}
                            </button>
                            <button className="share-modal-cancel-btn" onClick={handleCloseShareModal}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {ytWarningTooltip && (
                <div
                    className="yt-warning-popup"
                    style={{ top: ytWarningTooltip.y, left: ytWarningTooltip.x }}
                >
                    {ytWarningTooltip.type === 'qr' ? (
                        <strong>
                            This QR code has not been connected to a YouTube video. Go to Links → QR Code Generator and paste the link of the video on this QR code to connect it.
                        </strong>
                    ) : (
                        <>
                            <strong>A broken link.</strong>
                            <div className="yt-warning-popup-steps">
                                To fix this:
                                <br />1) Make sure you have connected your YouTube channel at the top of the page
                                <br />2) Click "Refresh YouTube Data" on the top right
                            </div>
                            <div className="yt-warning-popup-steps">
                                If this does not work, this is most likely a broken link. This can occur if someone modifies the URL parameter you have placed in your videos. You can delet this resource from the X on the left.
                            </div>
                            <p></p>
                            <span><i>If this is not a YouTube video, you can ignore this warning.</i></span>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default Dashboard;
